/**
 * chartTypes — pure helpers for the @ando/ui `<Chart>` wrapper.
 *
 * Ports the option-builder logic from v1
 * (`ando-frontend/app/utils/chartUtils.ts` :: `buildChartOption`) onto a
 * self-contained, side-effect-free surface so it can be unit-tested
 * WITHOUT ECharts, React, or jsdom in the loop. The wrapper component
 * (`Chart.tsx`) takes the option object this module returns and hands it
 * to `echarts-for-react`.
 *
 * Why pure: the same option-building runs in three different consumers
 * (dashboard cards, chat structured-data cards, inline enterprise panel)
 * and we want a single source of truth that's trivial to regression-test.
 *
 * SOTA references inherited from v1:
 *   - ECharts 5.2 `colorBy:'data'` for per-item bar/scatter colors
 *   - ECharts issue #19326: stacked-series workaround for per-category legend
 *   - top-N + "Other" aggregation for pie segments
 */

import type { EChartsOption } from "echarts";
import type { DataColumnMeta } from "../ando/DataPagedTable.js";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * The 7 chart types Ando renders. `table` is handled by the wrapper itself
 * via an HTML `<table>`; this builder returns `null` for it so the wrapper
 * knows to render the non-ECharts branch.
 */
export type ChartType = "line" | "area" | "bar" | "histogram" | "pie" | "scatter" | "table";

/**
 * Loose round-trip shape for the v1 ChartSpec visualization JSON. Kept open
 * here because the BE persists it verbatim — the v2 chart editor (PR-4)
 * will tighten this.
 */
export type ChartVisualization = Record<string, unknown>;

/** Preserve saved table-column order while ignoring stale or duplicate fields. */
export function resolveChartTableColumns(
  columns: ReadonlyArray<string>,
  viz?: ChartVisualization,
): ReadonlyArray<string> {
  if (!Array.isArray(viz?.table_columns)) return columns;
  const selected = [
    ...new Set(
      viz.table_columns.filter(
        (column): column is string => typeof column === "string" && columns.includes(column),
      ),
    ),
  ];
  return selected.length > 0 ? selected : columns;
}

/** Shared chart and detail-table metadata; malformed persisted entries are ignored. */
export function resolveChartColumnMeta(viz?: ChartVisualization): ReadonlyArray<DataColumnMeta> {
  return Array.isArray(viz?.column_meta)
    ? viz.column_meta.filter(
        (item): item is DataColumnMeta =>
          item !== null && typeof item === "object" && typeof item.name === "string",
      )
    : [];
}

// ---------------------------------------------------------------------------
// Ando theme
// ---------------------------------------------------------------------------

/**
 * Single source of truth for chart colours. Aligned with v1 CHART_COLORS
 * but pivoting the leading colour to the Ando orange (#FF7A1A) so the
 * primary series visually matches the rest of the product chrome.
 */
export const ANDO_CHART_COLORS: ReadonlyArray<string> = [
  "#FF7A1A", // Ando orange (accent)
  "#3B82F6", // Bright blue
  "#10B981", // Emerald
  "#8B5CF6", // Violet
  "#F59E0B", // Amber
  "#EC4899", // Pink
  "#06B6D4", // Cyan
  "#EF4444", // Red
  "#84CC16", // Lime
  "#6366F1", // Indigo
  "#F97316", // Orange
  "#14B8A6", // Teal
];

/**
 * Axis / tooltip styling tuned to the Ando cream + ink palette. Kept as a
 * tiny config object so the wrapper can merge it cheaply on every render.
 */
export const ANDO_CHART_THEME = {
  colors: ANDO_CHART_COLORS,
  axisLabel: { fontSize: 12, color: "#56544F" /* ink-2 */ },
  axisLine: { lineStyle: { color: "#BDBBB6" /* ink-4 */ } },
  splitLine: { lineStyle: { color: "#E5E7EB", opacity: 0.3 } },
  tooltip: {
    backgroundColor: "rgba(255, 255, 255, 0.98)",
    borderColor: "#E5E7EB",
    textStyle: { color: "#1F1D1A" /* ink-1 */, fontSize: 12 },
    extraCssText: "box-shadow: 0 4px 12px rgba(0,0,0,0.08); border-radius: 8px;",
  },
} as const;

// ---------------------------------------------------------------------------
// Internal helpers — pure, no React, no DOM
// ---------------------------------------------------------------------------

const MAX_PIE_SEGMENTS = 10;
const MIN_LABEL_PERCENT = 3;

interface PieDataItem {
  readonly name: string;
  readonly value: number;
  readonly children?: ReadonlyArray<PieDataItem>;
}

function formatNumber(v: unknown): string {
  const n = observationNumber(v);
  if (n === null) return "—";
  return n.toLocaleString("it-IT", { maximumFractionDigits: 2 });
}

/** Missing or invalid observations are gaps, not measured zeroes. */
function observationNumber(value: unknown): number | null {
  if (value == null || typeof value === "boolean") return null;
  if (typeof value !== "number" && typeof value !== "string") return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

/**
 * `≥50%` of non-null values must parse as finite numbers for a column to
 * be considered numeric. Filters out string fields (e.g. "Type", "Industry")
 * so they don't leak into the auto-detected Y series.
 */
function isNumericColumn(data: ReadonlyArray<Record<string, unknown>>, key: string): boolean {
  let nonNull = 0;
  let numericCount = 0;
  for (const row of data) {
    const v = row[key];
    if (v === null || v === undefined || v === "") continue;
    nonNull++;
    if (typeof v === "number" && Number.isFinite(v)) {
      numericCount++;
      continue;
    }
    if (typeof v === "string") {
      const n = Number(v);
      if (Number.isFinite(n)) numericCount++;
    }
  }
  if (nonNull === 0) return false;
  return numericCount / nonNull >= 0.5;
}

/** Resolve the actual rendered category column, shared with click filtering. */
export function resolveChartDimension(
  data: ReadonlyArray<Record<string, unknown>>,
  columns: ReadonlyArray<string>,
  viz?: ChartVisualization,
): string | undefined {
  if (!data[0]) return undefined;
  const keys = columns.length > 0 ? columns : Object.keys(data[0]);
  const legacyAxis = viz?.x_axis as { label?: string } | undefined;
  const requested = typeof viz?.x_field === "string" ? viz.x_field : legacyAxis?.label;
  if (requested && keys.includes(requested)) return requested;
  return keys.find((key) => !isNumericColumn(data, key)) ?? keys[0];
}

function isInvariantNumericColumn(
  data: ReadonlyArray<Record<string, unknown>>,
  key: string,
): boolean {
  if (data.length < 2 || !isNumericColumn(data, key)) return false;
  const values = new Set(
    data
      .map((row) => row[key])
      .filter((value) => value !== null && value !== undefined && value !== "")
      .map((value) => Number(value)),
  );
  return values.size === 1;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

interface TooltipParam {
  readonly axisValue?: unknown;
  readonly name?: unknown;
  readonly value?: unknown;
  readonly marker?: string;
  readonly seriesName?: unknown;
}

function axisTooltipFormatter(params: unknown): string {
  const list = (Array.isArray(params) ? params : [params]) as ReadonlyArray<TooltipParam>;
  const first = list[0];
  const catName = escapeHtml(String(first?.axisValue ?? first?.name ?? ""));
  const lines = list
    .filter((p) => p.value != null)
    .map(
      (p) =>
        `${p.marker ?? ""} ${escapeHtml(String(p.seriesName ?? ""))}: <b>${formatNumber(p.value)}</b>`,
    );
  return `<b>${catName}</b><br/>${lines.length ? lines.join("<br/>") : "—"}`;
}

function buildGrid(
  hasLegend: boolean,
  legendPosition?: string,
  hasXAxisTitle = false,
): EChartsOption["grid"] {
  const bottomLegend = hasLegend && legendPosition !== "top" && legendPosition !== "right";
  return {
    left: "3%",
    right: hasLegend && legendPosition === "right" ? 112 : "4%",
    top: hasLegend && legendPosition === "top" ? 48 : 32,
    // Axis titles sit outside the tick-label bounds. Reserve their own band
    // above the bottom legend instead of making both share percentage space.
    bottom: (bottomLegend ? 58 : 24) + (hasXAxisTitle ? 28 : 0),
    containLabel: true,
  };
}

function buildLegendConfig(
  position: string | undefined,
  itemCount: number,
): EChartsOption["legend"] {
  if (position === "none") return { show: false };
  const scrollType = itemCount > 6 ? ("scroll" as const) : ("plain" as const);
  switch (position) {
    case "top":
      return { top: 0, type: scrollType, textStyle: { fontSize: 12 } };
    case "right":
      return {
        orient: "vertical" as const,
        right: 0,
        top: "middle",
        type: scrollType,
        textStyle: { fontSize: 11 },
      };
    default:
      return { bottom: 0, type: scrollType, textStyle: { fontSize: 12 } };
  }
}

interface YScale {
  readonly min?: number;
  readonly max?: number;
  readonly log?: boolean;
}

type AxisSemantic = "percentage" | "monetary" | "numeric";

function axisValueFormatter(semantic: AxisSemantic): (value: number) => string {
  if (semantic === "percentage") return (value) => `${formatNumber(value)}%`;
  return (value) => formatNumber(value);
}

function buildYAxis(
  scale?: YScale,
  semantic: AxisSemantic = "numeric",
  position: "left" | "right" = "left",
): EChartsOption["yAxis"] {
  return {
    type: scale?.log ? "log" : "value",
    position,
    axisLabel: {
      ...ANDO_CHART_THEME.axisLabel,
      formatter: axisValueFormatter(semantic),
    },
    axisLine: ANDO_CHART_THEME.axisLine,
    splitLine: position === "left" ? ANDO_CHART_THEME.splitLine : { show: false },
    ...(scale?.min !== undefined ? { min: scale.min } : {}),
    ...(scale?.max !== undefined ? { max: scale.max } : {}),
  };
}

interface PieResult {
  readonly data: ReadonlyArray<PieDataItem>;
  readonly legend: EChartsOption["legend"];
  readonly label: Record<string, unknown>;
}

/**
 * Top-N + "Other" aggregation for pie segments + adaptive legend. Mirrors
 * v1 `preparePieData` so the visual output is identical.
 */
export function preparePieData(rawData: ReadonlyArray<PieDataItem>): PieResult {
  const sorted = [...rawData].sort((a, b) => b.value - a.value);
  let data: ReadonlyArray<PieDataItem>;
  if (sorted.length > MAX_PIE_SEGMENTS) {
    const top = sorted.slice(0, MAX_PIE_SEGMENTS);
    const rest = sorted.slice(MAX_PIE_SEGMENTS);
    const otherValue = rest.reduce((sum, d) => sum + d.value, 0);
    data = [...top, { name: `Other (${rest.length})`, value: otherValue, children: rest }];
  } else {
    data = sorted;
  }
  const segmentCount = data.length;
  let legend: EChartsOption["legend"];
  if (segmentCount <= 10) {
    legend = {
      bottom: 0,
      type: "plain" as const,
      textStyle: { fontSize: 12 },
    };
  } else if (segmentCount <= 20) {
    legend = {
      type: "scroll" as const,
      orient: "horizontal" as const,
      left: "center",
      right: "center",
      bottom: 0,
      width: "88%",
      textStyle: { fontSize: 11 },
      pageIconSize: 12,
      pageTextStyle: { fontSize: 10 },
    };
  } else {
    legend = { show: false };
  }
  const label: Record<string, unknown> = {
    show: true,
    formatter: (params: { name: string; percent: number }) =>
      params.percent >= MIN_LABEL_PERCENT ? `${params.name}\n${params.percent.toFixed(1)}%` : "",
    fontSize: 12,
  };
  return { data, legend, label };
}

// ---------------------------------------------------------------------------
// Public builder
// ---------------------------------------------------------------------------

export interface BuildOptionContext {
  /** When the system has `prefers-reduced-motion: reduce`, animation is disabled. */
  readonly reducedMotion?: boolean;
}

/**
 * Pure helper: takes (type, data, columns, viz, context) and returns the
 * ECharts option object. Returns `null` for `table` (handled by the wrapper)
 * or when `data` is empty.
 *
 * @param type     Chart type (one of the 7 supported variants)
 * @param data     Tabular data: ordered array of row objects
 * @param columns  Ordered column names (used as fallback when `viz` lacks
 *                 explicit x / y field hints)
 * @param viz      Optional ChartSpec visualization blob (v1 round-trip shape)
 * @param context  Render-time flags (reducedMotion, etc.)
 */
export function buildOption(
  type: ChartType,
  data: ReadonlyArray<Record<string, unknown>>,
  columns: ReadonlyArray<string>,
  viz?: ChartVisualization,
  context?: BuildOptionContext,
): EChartsOption | null {
  // Table type is rendered by the wrapper's HTML branch, not by ECharts.
  if (type === "table") return null;
  if (!data || data.length === 0) return null;

  const v: ChartVisualization = viz ?? {};
  const xAxis = v.x_axis as { label?: string } | undefined;
  const xField = (v.x_field as string | undefined) ?? xAxis?.label;
  const yFields = (v.y_fields as ReadonlyArray<string> | undefined) ?? [];
  const groupBy = v.group_by as string | undefined;
  const isStacked = v.stacked === true;
  const axis = v.axis as { xLabel?: string; yLabel?: string; legend?: boolean } | undefined;
  const legendPos =
    axis?.legend === false
      ? "none"
      : ((v.legend_position as string | undefined) ??
        (axis?.legend === true ? "bottom" : undefined));
  const colorConfig = v.colors;
  const customColors =
    Array.isArray(colorConfig) && colorConfig.length > 0 ? (colorConfig as string[]) : undefined;
  const namedColors =
    colorConfig && typeof colorConfig === "object" && !Array.isArray(colorConfig)
      ? (colorConfig as Record<string, string>)
      : {};
  const yScale = v.y_scale as YScale | undefined;
  const sortBy = v.sort_by as { field: string; order: "asc" | "desc" } | undefined;
  const columnMeta = Array.isArray(v.column_meta)
    ? (v.column_meta as ReadonlyArray<{
        name?: unknown;
        role?: unknown;
        semantic_type?: unknown;
      }>)
    : [];
  const metaByField = new Map(
    columnMeta.flatMap((item) =>
      typeof item?.name === "string" ? [[item.name, item] as const] : [],
    ),
  );
  const declaredAxes =
    v.series_axes && typeof v.series_axes === "object"
      ? (v.series_axes as Readonly<Record<string, unknown>>)
      : {};

  // Optional sort. Numeric path falls back to localeCompare for non-numeric.
  let effectiveData: ReadonlyArray<Record<string, unknown>> = data;
  if (sortBy?.field) {
    const field = sortBy.field;
    const order = sortBy.order;
    effectiveData = [...data].sort((a, b) => {
      const va = a[field];
      const vb = b[field];
      const na = va === "" || va == null ? Number.NaN : Number(va);
      const nb = vb === "" || vb == null ? Number.NaN : Number(vb);
      const cmp =
        !Number.isNaN(na) && !Number.isNaN(nb)
          ? na - nb
          : String(va ?? "").localeCompare(String(vb ?? ""));
      return order === "desc" ? -cmp : cmp;
    });
  }

  // Auto-detect x / y from the column list when viz doesn't specify.
  const firstRow = effectiveData[0];
  if (!firstRow) return null;
  const allKeys = columns.length > 0 ? Array.from(columns) : Object.keys(firstRow);
  // Explicit selections are authoritative; infer only absent or stale fields.
  const hasExplicitX = typeof xField === "string" && allKeys.includes(xField);
  const effectiveX = resolveChartDimension(effectiveData, columns, viz);
  if (effectiveX === undefined) return null;

  const numericKeys = allKeys.filter(
    (key) =>
      key !== effectiveX &&
      isNumericColumn(effectiveData, key) &&
      metaByField.get(key)?.role !== "dimension",
  );
  const varyingNumericKeys = numericKeys.filter(
    (key) => !isInvariantNumericColumn(effectiveData, key),
  );
  let effectiveY: ReadonlyArray<string>;
  const validYFields = yFields.filter((key) => key !== effectiveX && allKeys.includes(key));
  if (validYFields.length > 0) {
    effectiveY = validYFields;
  } else {
    effectiveY =
      varyingNumericKeys.length > 0
        ? varyingNumericKeys
        : numericKeys.length > 0
          ? numericKeys
          : allKeys.filter((key) => key !== effectiveX);
  }

  // A categorical comparison is most useful in measure order. Keep explicit
  // SQL/viz ordering authoritative; this is only the chart's local view and
  // never mutates the complete table dataset.
  if (!sortBy && !hasExplicitX && (type === "bar" || type === "pie") && effectiveY[0]) {
    const primaryMeasure = effectiveY[0];
    effectiveData = [...effectiveData].sort(
      (a, b) => Number(b[primaryMeasure] ?? 0) - Number(a[primaryMeasure] ?? 0),
    );
  }

  const palette = customColors ?? ANDO_CHART_COLORS;
  const getColor = (idx: number, name?: string): string =>
    (name ? namedColors[name] : undefined) ??
    palette[idx % palette.length] ??
    ANDO_CHART_COLORS[0] ??
    "#FF7A1A";

  const animation = !context?.reducedMotion;
  const animationDuration = context?.reducedMotion ? 0 : 300;

  // ---- Group-by handling ----
  if (groupBy && groupBy !== effectiveX && effectiveY.length === 1 && type !== "pie") {
    const yKey = effectiveY[0];
    if (!yKey) return null;
    const groups = new Map<string, Map<string, number>>();
    const categorySet = new Set<string>();
    for (const row of effectiveData) {
      const cat = String(row[effectiveX] ?? "");
      const grp = String(row[groupBy] ?? "");
      categorySet.add(cat);
      let m = groups.get(grp);
      if (!m) {
        m = new Map<string, number>();
        groups.set(grp, m);
      }
      const value = observationNumber(row[yKey]);
      if (value !== null) m.set(cat, (m.get(cat) ?? 0) + value);
    }
    const cats = Array.from(categorySet);
    const groupNames = Array.from(groups.keys());
    const seriesType: "bar" | "line" | "scatter" =
      type === "area" ? "line" : type === "scatter" ? "scatter" : type === "line" ? "line" : "bar";
    const series = groupNames.map((grpName, idx) => {
      const m = groups.get(grpName);
      return {
        name: grpName,
        type: seriesType,
        data: cats.map((cat) => m?.get(cat) ?? null),
        ...(isStacked ? { stack: "grouped" } : {}),
        ...(type === "line" || type === "area" ? { smooth: true } : {}),
        ...(type === "area" ? { areaStyle: { opacity: 0.3 } } : {}),
        itemStyle: {
          color: getColor(idx, namedColors[grpName] ? grpName : yKey),
          ...(type === "bar" || type === "histogram"
            ? { borderRadius: [2, 2, 0, 0] as [number, number, number, number] }
            : {}),
        },
      };
    });
    const lgndConfig = buildLegendConfig(legendPos, groupNames.length);
    return {
      animation,
      animationDuration,
      color: Array.from(palette),
      tooltip: {
        ...ANDO_CHART_THEME.tooltip,
        trigger: "axis" as const,
        formatter: axisTooltipFormatter,
      },
      grid: buildGrid(!!(lgndConfig && (lgndConfig as { show?: boolean }).show !== false), legendPos, Boolean(axis?.xLabel)),
      xAxis: {
        type: "category",
        ...(axis?.xLabel
          ? { name: axis.xLabel, nameLocation: "middle" as const, nameGap: 34 }
          : {}),
        data: cats,
        axisLabel: {
          ...ANDO_CHART_THEME.axisLabel,
          rotate: cats.length > 6 ? 30 : 0,
          overflow: "truncate" as const,
          width: 100,
        },
        axisLine: ANDO_CHART_THEME.axisLine,
      },
      yAxis: { ...buildYAxis(yScale), ...(axis?.yLabel ? { name: axis.yLabel } : {}) },
      legend: lgndConfig,
      series,
    };
  }

  const categories = effectiveData.map((row) => String(row[effectiveX] ?? ""));
  const isSingleY = effectiveY.length === 1;
  const semanticFor = (field: string): AxisSemantic => {
    const semantic = metaByField.get(field)?.semantic_type;
    return semantic === "percentage" || semantic === "monetary" ? semantic : "numeric";
  };
  const hasPrimarySeries = effectiveY.some(
    (field) => declaredAxes[field] !== "secondary" && semanticFor(field) !== "percentage",
  );
  const secondaryFields = new Set(
    effectiveY.filter(
      (field) =>
        declaredAxes[field] === "secondary" ||
        (semanticFor(field) === "percentage" && hasPrimarySeries),
    ),
  );
  const primarySemantic = semanticFor(
    effectiveY.find((field) => !secondaryFields.has(field)) ?? effectiveY[0] ?? "",
  );
  const usesSecondaryAxis = secondaryFields.size > 0 && secondaryFields.size < effectiveY.length;

  // ---- Pie ----
  if (type === "pie") {
    if (effectiveY.length === 0) return null;
    const yField = effectiveY[0];
    if (!yField) return null;
    const rawPieData: ReadonlyArray<PieDataItem> = effectiveData.flatMap((row) => {
      const value = observationNumber(row[yField]);
      return value === null ? [] : [{ name: String(row[effectiveX] ?? ""), value }];
    });
    const { data: pieData, legend, label } = preparePieData(rawPieData);
    const hasScrollLegend = pieData.length > 10;
    return {
      animation,
      animationDuration,
      color: Array.from(palette),
      tooltip: {
        ...ANDO_CHART_THEME.tooltip,
        trigger: "item",
        formatter: "{b}: {c} ({d}%)",
      },
      legend: legendPos ? buildLegendConfig(legendPos, pieData.length) : legend,
      // ECharts media queries use the chart container, not viewport width.
      // On narrow cards the legend carries category names; outside labels
      // cannot fit beside a doughnut and would clip into neighboring cards.
      media: [
        {
          query: { maxWidth: 360 },
          option: {
            legend: {
              show: legendPos !== "none",
              type: "scroll",
              orient: "horizontal",
              left: 0,
              right: 0,
              top: "auto",
              bottom: 0,
              textStyle: { fontSize: 11, width: 110, overflow: "truncate" },
              pageIconSize: 11,
              pageTextStyle: { fontSize: 10 },
            },
            series: [{
              type: "pie",
              center: ["50%", "42%"],
              radius: ["30%", "60%"],
              label: { show: false },
              labelLine: { show: false },
              emphasis: { label: { show: false } },
            }],
          },
        },
        {
          query: { minWidth: 361 },
          option: {
            legend: legendPos ? buildLegendConfig(legendPos, pieData.length) : legend,
            series: [{
              type: "pie",
              center: [legendPos === "right" ? "40%" : "50%", hasScrollLegend ? "43%" : "45%"],
              radius: hasScrollLegend ? ["30%", "55%"] : ["35%", "62%"],
              label,
              labelLine: { show: true, length: 12, length2: 6 },
            }],
          },
        },
      ],
      series: [
        {
          type: "pie",
          radius: hasScrollLegend ? ["30%", "55%"] : ["35%", "62%"],
          center: [legendPos === "right" ? "40%" : "50%", hasScrollLegend ? "43%" : "45%"],
          data: pieData.map((d) => ({
            ...d,
            ...(namedColors[d.name] ? { itemStyle: { color: namedColors[d.name] } } : {}),
          })),
          itemStyle: { borderRadius: 4, borderColor: "#fff", borderWidth: 2 },
          label,
          labelLine: { show: true, length: 12, length2: 6 },
          labelLayout: { hideOverlap: true, moveOverlap: "shiftY" },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: "rgba(0, 0, 0, 0.3)",
            },
          },
        },
      ],
    };
  }

  // An explicitly chosen numeric X is a measured coordinate, not an evenly
  // spaced category. Keep legacy categorical scatter behavior when X is text.
  if (type === "scatter" && hasExplicitX && isNumericColumn(effectiveData, effectiveX)) {
    const legend = legendPos ? buildLegendConfig(legendPos, effectiveY.length) : { show: false };
    return {
      animation,
      animationDuration,
      color: Array.from(palette),
      grid: buildGrid((legend as { show?: boolean }).show !== false, legendPos),
      tooltip: {
        ...ANDO_CHART_THEME.tooltip,
        trigger: "item",
        formatter: (raw: unknown) => {
          const point = raw as { seriesName?: string; value?: [number, number] };
          return `${escapeHtml(point.seriesName ?? "")}<br/>${escapeHtml(axis?.xLabel || effectiveX)}: <b>${formatNumber(point.value?.[0])}</b><br/>${escapeHtml(axis?.yLabel || "Y")}: <b>${formatNumber(point.value?.[1])}</b>`;
        },
      },
      xAxis: {
        type: "value",
        name: axis?.xLabel || effectiveX,
        axisLabel: ANDO_CHART_THEME.axisLabel,
        axisLine: ANDO_CHART_THEME.axisLine,
        splitLine: ANDO_CHART_THEME.splitLine,
      },
      yAxis: {
        ...buildYAxis(yScale, primarySemantic),
        ...(axis?.yLabel ? { name: axis.yLabel } : {}),
      } as EChartsOption["yAxis"],
      legend,
      series: effectiveY.map((yKey, index) => ({
        name: yKey,
        type: "scatter" as const,
        symbolSize: 10,
        itemStyle: { color: getColor(index, yKey) },
        data: effectiveData.flatMap((row) => {
          const x = observationNumber(row[effectiveX]);
          const y = observationNumber(row[yKey]);
          return x === null || y === null ? [] : [{ name: String(row[effectiveX]), value: [x, y] }];
        }),
      })),
    };
  }

  // ---- Series for bar / line / area / scatter / histogram ----
  const seriesType: "bar" | "line" | "scatter" =
    type === "area"
      ? "line"
      : type === "histogram"
        ? "bar"
        : type === "scatter"
          ? "scatter"
          : type === "line"
            ? "line"
            : "bar";

  let series: EChartsOption["series"];
  let legendConfig: EChartsOption["legend"];

  if (isSingleY && seriesType === "bar") {
    // A measure is one series regardless of category count. Category legends
    // duplicate the X axis and make a palette override change meaning at 20 rows.
    const yKey = effectiveY[0];
    if (!yKey) return null;
    series = [
      {
        name: yKey,
        type: "bar" as const,
        data: effectiveData.map((row) => observationNumber(row[yKey])),
        itemStyle: {
          color: getColor(0, yKey),
          borderRadius: [2, 2, 0, 0] as [number, number, number, number],
        },
      },
    ];
    legendConfig = legendPos ? buildLegendConfig(legendPos, 1) : { show: false };
  } else if (isSingleY && seriesType === "scatter") {
    const yKey = effectiveY[0];
    if (!yKey) return null;
    series = [
      {
        name: yKey,
        type: "scatter" as const,
        data: effectiveData.map((row) => observationNumber(row[yKey])),
        itemStyle: { color: getColor(0, yKey) },
        symbolSize: 10,
      },
    ];
    legendConfig = legendPos ? buildLegendConfig(legendPos, 0) : { show: false };
  } else if (isSingleY && seriesType === "line") {
    const yKey = effectiveY[0];
    if (!yKey) return null;
    series = [
      {
        name: yKey,
        type: "line" as const,
        data: effectiveData.map((row) => observationNumber(row[yKey])),
        smooth: true,
        showSymbol: true,
        symbolSize: 6,
        ...(type === "area" ? { areaStyle: { opacity: 0.3 } } : {}),
        itemStyle: { color: getColor(0, yKey) },
        lineStyle: { width: 3 },
      },
    ];
    legendConfig = legendPos ? buildLegendConfig(legendPos, 1) : { show: false };
  } else {
    // Multi-Y: one series per field, regular legend.
    series = effectiveY.map((yKey, idx) => ({
      name: yKey,
      type: seriesType,
      ...(usesSecondaryAxis ? { yAxisIndex: secondaryFields.has(yKey) ? 1 : 0 } : {}),
      data: effectiveData.map((row) => observationNumber(row[yKey])),
      ...(seriesType === "line" ? { smooth: true, showSymbol: true } : {}),
      ...(seriesType === "scatter" ? { symbolSize: 10 } : { symbolSize: 6 }),
      ...(type === "area" ? { areaStyle: { opacity: 0.3 } } : {}),
      ...(seriesType === "line"
        ? {
            lineStyle: {
              width: secondaryFields.has(yKey) ? 2 : 3,
              ...(secondaryFields.has(yKey) ? { type: "dashed" as const } : {}),
            },
          }
        : {}),
      ...(isStacked ? { stack: "stacked" } : {}),
      itemStyle: {
        color: getColor(idx, yKey),
        ...(seriesType === "bar"
          ? { borderRadius: [2, 2, 0, 0] as [number, number, number, number] }
          : {}),
      },
    }));
    legendConfig = buildLegendConfig(legendPos, effectiveY.length);
  }

  const hasLegend = legendConfig && (legendConfig as { show?: boolean }).show !== false;
  const baseGrid = buildGrid(!!hasLegend, legendPos, Boolean(axis?.xLabel));

  const result: EChartsOption = {
    animation,
    animationDuration,
    color: Array.from(palette),
    tooltip: {
      ...ANDO_CHART_THEME.tooltip,
      trigger: "axis" as const,
      formatter: axisTooltipFormatter,
    },
    grid: baseGrid,
    xAxis: {
      type: "category",
      ...(axis?.xLabel ? { name: axis.xLabel, nameLocation: "middle" as const, nameGap: 34 } : {}),
      data: categories,
      axisLabel: {
        ...ANDO_CHART_THEME.axisLabel,
        rotate: categories.length > 6 ? 30 : 0,
        overflow: "truncate" as const,
        width: 100,
      },
      axisLine: ANDO_CHART_THEME.axisLine,
    },
    yAxis: (usesSecondaryAxis
      ? [
          {
            ...buildYAxis(yScale, primarySemantic, "left"),
            ...(axis?.yLabel ? { name: axis.yLabel } : {}),
          },
          buildYAxis(undefined, "percentage", "right"),
        ]
      : {
          ...buildYAxis(yScale, primarySemantic),
          ...(axis?.yLabel ? { name: axis.yLabel } : {}),
        }) as EChartsOption["yAxis"],
    legend: legendConfig,
    series,
  };

  // Auto dataZoom when the category count is large (V1 parity: 50+).
  if (categories.length > 50) {
    const initialEnd = Math.min(100, (50 / categories.length) * 100);
    return {
      ...result,
      dataZoom: [
        { type: "inside", start: 0, end: initialEnd },
        { type: "slider", start: 0, end: initialEnd, height: 20, bottom: 0 },
      ],
      grid: { ...baseGrid, bottom: 84 + (axis?.xLabel ? 28 : 0) },
    };
  }
  return result;
}
